import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { confirmSignUp, getCurrentUser, signIn, signOut, signUp } from 'aws-amplify/auth';

// Authenticated creator, derived from the Cognito session. The application
// never sends this identity to the API in request payloads: the GraphQL_API
// derives the creator identity from the validated JWT (Req 1.5, 1.6). This
// value is used only for UI display and the signed-in gate.
export interface AuthUser {
  readonly userId: string;
  readonly username: string;
}

export interface AuthContextValue {
  readonly user: AuthUser | undefined;
  readonly isAuthenticated: boolean;
  readonly isLoading: boolean;
  signIn(email: string, password: string): Promise<void>;
  signUp(email: string, password: string): Promise<{ confirmationRequired: boolean }>;
  confirmSignUp(email: string, code: string): Promise<void>;
  signOut(): Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }): ReactNode {
  const [user, setUser] = useState<AuthUser | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    try {
      const current = await getCurrentUser();
      setUser({ userId: current.userId, username: current.username });
    } catch {
      setUser(undefined);
    }
  }, []);

  useEffect(() => {
    void (async () => {
      await refreshUser();
      setIsLoading(false);
    })();
  }, [refreshUser]);

  const handleSignIn = useCallback(
    async (email: string, password: string) => {
      // Sign out any partial/stale session first so signIn does not throw
      // "already signed in".
      try {
        await signOut();
      } catch {
        // ignore: no active session to clear
      }
      await signIn({ username: email, password });
      await refreshUser();
    },
    [refreshUser],
  );

  const handleSignUp = useCallback(async (email: string, password: string) => {
    const result = await signUp({
      username: email,
      password,
      options: { userAttributes: { email } },
    });
    return { confirmationRequired: !result.isSignUpComplete };
  }, []);

  const handleConfirmSignUp = useCallback(async (email: string, code: string) => {
    await confirmSignUp({ username: email, confirmationCode: code });
  }, []);

  const handleSignOut = useCallback(async () => {
    await signOut();
    setUser(undefined);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated: user !== undefined,
      isLoading,
      signIn: handleSignIn,
      signUp: handleSignUp,
      confirmSignUp: handleConfirmSignUp,
      signOut: handleSignOut,
    }),
    [user, isLoading, handleSignIn, handleSignUp, handleConfirmSignUp, handleSignOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (ctx === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
